export const LANDING = "/landing";
export const LANDING_PHONE_NUMBER = "/landing-phone-number";
export const LOGIN = "/login";
export const LOGIN_PHONE_NUMBER = "/login-phone-number";

export const SIGN_UP = "/sign-up";
export const SIGN_UP_PHONE_NUMBER = "/sign-up-phone-number";
export const VERIFY_PHONE_NUMBER = "/verify-phone-number";
export const COMPLETE_PROFILE = "/complete-profile";

/** Login / landing / signup paths wrapped by UnauthenticatedRoute. */
export const isPublicAuthPath = (path: string): boolean =>
  path === LANDING ||
  path === LANDING_PHONE_NUMBER ||
  path === LOGIN ||
  path === LOGIN_PHONE_NUMBER ||
  path === SIGN_UP ||
  path === SIGN_UP_PHONE_NUMBER;

export const HOME = "/home";
export const HOME_PAGE = "/home/home";
export const DASHBOARD = "/home/dashboard";

export const SHOP = "/home/shop";
export const SHOP_PRODUCT_DETAILS = "/home/shop/product/:handle";
export const SHOP_PRODUCT_DETAILS_BASE = SHOP_PRODUCT_DETAILS.replace(
  ":handle",
  "",
);
export const SHOP_CART = "/home/shop/cart";
export const SHOP_CHECKOUT = "/home/shop/checkout";
export const SHOP_ORDER_SUCCESS = "/home/shop/order-success";
export const DISCOUNTS = "/home/discounts";
export const DISCOUNT_DETAILS = "/home/discounts/:discountId";
export const DISCOUNT_DETAILS_BASE = DISCOUNT_DETAILS.replace(
  ":discountId",
  "",
);

/** Paths inside the shop flow — not valid "exit shop" back targets. */
export const isShopStackPath = (path: string): boolean =>
  path === SHOP ||
  path === SHOP_CART ||
  path === SHOP_CHECKOUT ||
  path.startsWith(SHOP_PRODUCT_DETAILS_BASE);

/**
 * Discount details URLs (`/home/discounts/:id`) — not valid exit targets from
 * Save-list `ShopSubHeader` back (would bounce list → details again).
 */
export const isDiscountDetailsPath = (path: string): boolean =>
  path.startsWith(DISCOUNT_DETAILS_BASE) &&
  path !== DISCOUNTS &&
  path !== DISCOUNT_DETAILS_BASE;

/** Shop-only paths (Discounts excluded) — used to redirect when shop is disabled. */
export const isShopOnlyPath = (path: string): boolean =>
  path === SHOP ||
  path === SHOP_CART ||
  path === SHOP_CHECKOUT ||
  path.startsWith(SHOP_PRODUCT_DETAILS_BASE);
export const GIVEAWAYS = "/home/giveaways";
export const GIVEAWAYS_CURRENT_VIEW = "/home/giveaways-current";
export const GIVEAWAYS_UPCOMING_VIEW = "/home/giveaways-upcoming";
export const GIVEAWAYS_UPCOMING_LIST = "/home/giveaways-upcoming-list";
export const GIVEAWAYS_PAST_VIEW = "/home/giveaways-past";
export const GIVEAWAYS_PAST_LIST = "/home/giveaways-past-list";

export const LOCKER = "/home/locker";
export const CALENDAR = "/home/calendar";
export const REWARDS = "/home/rewards";
export const GAMES = "/home/games";
export const SAVINGS_SERIES_LIST = "/home/savings-series-list";
export const SAVINGS_WINNERS_LADDER = "/home/savings-ladder";
export const SAVINGS_WINNERS_LADDER_PARAM =
  "/home/savings-ladder/:seriesId/:seasonId";
export const SAVINGS_PREVIOUS_WINNERS_LADDER =
  "/home/previous/savings-ladder/:seriesId/:seasonId";
const SAVINGS_PREVIOUS_WINNERS_LADDER_BASE = "/home/previous/savings-ladder";
const SAVINGS_LADDER_BASE = "/home/savings-ladder";

export const buildSavingsWinnersLadderPath = (
  seriesId: string,
  seasonId: string
): string => `/home/savings-ladder/${seriesId}/${seasonId}`;

export const buildSavingsPreviousWinnersLadderPath = (
  seriesId: string,
  seasonId: string
): string => `/home/previous/savings-ladder/${seriesId}/${seasonId}`;

export const isSavingsLeaderboardPath = (path: string): boolean =>
  path === SAVINGS_SERIES_LIST ||
  path === SAVINGS_WINNERS_LADDER ||
  path.startsWith(SAVINGS_LADDER_BASE) ||
  path.startsWith(SAVINGS_PREVIOUS_WINNERS_LADDER_BASE);

/** Save list sub-header back must not rebound into drilldowns (details, series, ladders). */
export const isSaveListHistoryReboundTarget = (path: string): boolean =>
  isDiscountDetailsPath(path) || isSavingsLeaderboardPath(path);

export const WINNERS_LADDER = "/home/winners-ladder";
export const PREVIOUS_WINNERS_LADDER =
  "/home/previous/winners-ladder/:seasonId";
const PREVIOUS_WINNERS_LADDER_BASE = "/home/previous/winners-ladder";

export const buildGamePreviousWinnersLadderPath = (seasonId: string): string =>
  `/home/previous/winners-ladder/${seasonId}`;

export const isPastWinnersLadderPath = (path: string): boolean =>
  path.startsWith(PREVIOUS_WINNERS_LADDER_BASE) ||
  path.startsWith(SAVINGS_PREVIOUS_WINNERS_LADDER_BASE);

/** Live savings or gaming ladder boards — not the series hub. */
export const isCurrentWinnersLadderPath = (path: string): boolean =>
  path === WINNERS_LADDER ||
  path === SAVINGS_WINNERS_LADDER ||
  path.startsWith(`${SAVINGS_LADDER_BASE}/`);

export const isWinnersLadderBoardPath = (path: string): boolean =>
  isPastWinnersLadderPath(path) || isCurrentWinnersLadderPath(path);

/** Games-only paths — used to redirect when games is disabled. */
export const isGamesOnlyPath = (path: string): boolean =>
  path === GAMES ||
  path === WINNERS_LADDER ||
  path.startsWith(PREVIOUS_WINNERS_LADDER_BASE);

export const ACCOUNT = "/home/account";
export const USER_PROFILE = "/home/account/user-profile";
export const REFER_A_MATE = "/home/account/refer-a-mate";
export const BILLING = "/home/account/billing";
export const SIGN_IN_SECURITY = "/home/account/sign-in-security";
export const CHANGE_EMAIL = "/home/account/change-email";
export const CHANGE_PASSWORD = "/home/account/change-password";
export const CHANGE_PHONE_NUMBER = "/home/account/change-phone-number";
export const USER_SUBSCRIPTION = "/home/account/subscription";
export const USER_PAYMENT_METHODS = "/home/account/payment-methods";
export const POSTAL_INFORMATION = "/home/account/postal-information";
export const PURCHASE_ENTRIES = "/home/dashboard/purchase-entries";

export const POWER_UP = "/power-up";

export const PAYMENT_PAGE = "/payment";

/** Off-shell flows that must not be shop exit-back targets (e.g. Power Up pitch → Shop). */
export const isInvalidShopExitBackTarget = (path: string): boolean =>
  path === POWER_UP || path === PAYMENT_PAGE;

export const SURVEY_QUESTIONNAIRE = "/survey-questionnaire";

export const WEB_SIGN_UP = "/join/:subscriptionKey";

export const UPDATE_PHONE_NUMBER_VERIFY_CHOOSER =
  "/phone-change-verification/verify-chooser";

export const UPDATE_PHONE_NUMBER_VERIFY_BY_PASSWORD =
  "/phone-change-verification/verify-password";

export const UPDATE_PHONE_NUMBER_VERIFY_BY_OTP =
  "/phone-change-verification/verify-otp";

// should be common
export const RESET_PASSWORD = "/auth/action";
export const FORGOT_PASSWORD = "/forgot-password";
// dont know whats this for? check src/routes/DirectRouteListener
export const EXCLUDED_FROM_ROUTE_SAVING = [
  SIGN_UP,
  SIGN_UP_PHONE_NUMBER,
  LANDING,
  LOGIN,
  LOGIN_PHONE_NUMBER,
  "/join/starter",
  "/join/mvp",
  "/join/pro",
  "/join/club",
  "/enter/bronze",
  "/enter/silver",
  "/enter/gold",
  "/enter/platinum",
  "/enter/diamond",
  HOME,
  DASHBOARD,
  RESET_PASSWORD,
  FORGOT_PASSWORD,
  // "/",
];

export const EXCLUDED_FROM_REDIRECT = [
  SIGN_UP,
  SIGN_UP_PHONE_NUMBER,
  LOGIN_PHONE_NUMBER,
  LANDING_PHONE_NUMBER,
  VERIFY_PHONE_NUMBER,
  COMPLETE_PROFILE,
  LANDING,
  LOGIN,
  LOGIN_PHONE_NUMBER,
  RESET_PASSWORD,
  FORGOT_PASSWORD,
  // "/",
];

export const UNAUTHENTICATED_ROUTES = [RESET_PASSWORD, SIGN_UP, LANDING];
